import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { HelpCircle, Book, MessageCircle, Mail, Phone, ExternalLink, Search, ChevronRight } from 'lucide-react';

const FAQ_ITEMS = [
  { q: 'How does ambient listening work?', a: 'Ambient listening passively captures the conversation between you and your patient. Simply start the session and speak naturally. Lumina will transcribe and structure the conversation into a SOAP note.' },
  { q: 'Is my patient data secure?', a: 'Yes! All data is encrypted in transit and at rest. Lumina is fully HIPAA compliant with complete audit trails for all documentation.' },
  { q: 'Can I edit the AI-generated notes?', a: 'Absolutely. All AI-generated content is fully editable. You have complete control over the final documentation before signing.' },
  { q: 'How accurate is the coding suggestion?', a: 'Our AI achieves 98%+ accuracy on coding suggestions. However, we always recommend reviewing codes before submission.' },
];

const RESOURCES = [
  { title: 'Getting Started Guide', desc: 'Learn the basics of Lumina', icon: Book },
  { title: 'Video Tutorials', desc: 'Watch step-by-step guides', icon: ExternalLink },
  { title: 'Best Practices', desc: 'Tips for optimal documentation', icon: Book },
  { title: 'Release Notes', desc: 'See what\'s new in Lumina', icon: ExternalLink },
];

const LuminaHelp = () => {
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="help-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Help & Support</h1>
        <p className="text-slate-500 mt-1">Find answers and get assistance</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search for help..."
              className="pl-12 h-12 text-lg"
            />
          </div>
        </CardContent>
      </Card>

      {/* Quick Resources */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {RESOURCES.map((resource, i) => (
          <Card key={i} className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <resource.icon className="h-8 w-8 text-emerald-600 mb-3" />
              <p className="font-medium">{resource.title}</p>
              <p className="text-sm text-slate-500">{resource.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900 mb-2">{item.q}</p>
              <p className="text-sm text-slate-600">{item.a}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Contact Support</CardTitle>
          <CardDescription>Can't find what you're looking for? Reach out to our team.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <MessageCircle className="h-6 w-6 text-emerald-600" />
              <div>
                <p className="font-medium">Live Chat</p>
                <p className="text-sm text-slate-500">Available 24/7</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Mail className="h-6 w-6 text-emerald-600" />
              <div>
                <p className="font-medium">Email</p>
                <p className="text-sm text-slate-500">support@lumina.health</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Phone className="h-6 w-6 text-emerald-600" />
              <div>
                <p className="font-medium">Phone</p>
                <p className="text-sm text-slate-500">1-800-LUMINA</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input placeholder="Your name" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input type="email" placeholder="your@email.com" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message</label>
              <Textarea placeholder="Describe your issue or question..." rows={4} />
            </div>
            <Button className="bg-emerald-600 hover:bg-emerald-700">Send Message</Button>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-slate-500">
            Lumina Clinical Intelligence • Built by ariaone • Version 1.0.0
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LuminaHelp;
